// Sorting helpers for the Emails tab: tag a conversation with the
// account's sites it mentions, and a rough type (Support / Billing /
// Setup / Reports / Other), both from wording in the subject lines.

const norm = (s) => ` ${String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;

// Words that don't identify a site on their own (brand, company, generic).
const GENERIC = new Set(`
  petro canada petrocanada esso shell husky ultramar mobil chevron pioneer fas gas co op coop sunoco parkland
  irving domo centex bar station store stores inc ltd limited the and of site location head office convenience
  mart market food foods fuel fuels petroleum truck stop car wash`.split(/\s+/).filter(Boolean));

export function buildSiteMatchers(sites, company) {
  const companyWords = new Set(norm(company).trim().split(' ').filter(Boolean));
  const cityCount = {};
  for (const s of sites) {
    const c = norm(s.city).trim();
    if (c) cityCount[c] = (cityCount[c] || 0) + 1;
  }
  const list = sites.map((s) => {
    const codes = [s.site_number, s.xsiteio_site_id]
      .map((v) => norm(v).trim())
      .filter((v) => v.length >= 3);
    const nameWords = norm(s.site_name).trim().split(' ')
      .filter((w) => w && !GENERIC.has(w) && !companyWords.has(w) && !codes.includes(w));
    const phrase = nameWords.join(' ');
    const phrases = [];
    if (phrase.length >= 4 && !/^\d+$/.test(phrase)) phrases.push(phrase);
    const city = norm(s.city).trim();
    if (city.length >= 4 && cityCount[city] === 1 && !phrases.includes(city)) phrases.push(city);
    return {
      id: s.id,
      label: s.site_name || s.site_number || `Site #${s.id}`,
      number: s.site_number || '',
      codes,
      phrases
    };
  }).filter((m) => m.codes.length || m.phrases.length);
  list.stop = companyWords;
  return list;
}

// Drop brand/company words from a subject so "BC Centex Koodoos" reads
// as "bc koodoos", the same way the site names were reduced.
function strip(text, stop) {
  return ` ${norm(text).trim().split(' ').filter((w) => w && !GENERIC.has(w) && !(stop && stop.has(w))).join(' ')} `;
}

// subjectText: subjects only (names). codeText: subjects + previews (codes
// like CEN00145 are distinctive enough to trust in the body).
export function matchSites(matchers, subjectText, codeText) {
  const subj = strip(subjectText, matchers.stop);
  const all = norm(codeText);
  // A site code (CEN00145, site #) is definitive: if any match, use only those.
  const byCode = matchers.filter((m) => m.codes.some((c) => all.includes(` ${c} `))).map((m) => m.id);
  if (byCode.length) return byCode;
  const hits = [];
  for (const m of matchers) {
    const p = m.phrases.filter((x) => subj.includes(` ${x} `)).sort((a, b) => b.length - a.length)[0];
    if (p) hits.push({ id: m.id, p });
  }
  // "bc koodoos" beats "koodoos" when both match the same words.
  return hits
    .filter((h) => !hits.some((o) => o !== h && o.p.length > h.p.length && ` ${o.p} `.includes(` ${h.p} `)))
    .map((h) => h.id);
}

export const CATEGORIES = ['Support', 'Billing', 'Setup', 'Reports', 'Other'];

const RULES = [
  ['Support', /\b(malfunction\w*|not working|doesn'?t work|error\w*|issues?|problems?|down|offline|broken|fix\w*|unable|can'?t|cannot|fail\w*|bugs?|crash\w*|help|ticket|urgent|missing|stuck|not showing|not updating)\b/i],
  ['Billing', /\b(invoices?|remittance|eft|payments?|paid|pay|statements?|renew\w*|pricing|price increase|overdue|subscriptions?|quotes?|receipts?|billing|credit card|refund\w*|balance|e-?transfer)\b/i],
  ['Setup', /\b(set ?up|install\w*|onboard\w*|new site|new store|configur\w*|sftp|training|go[- ]live|credentials?|log ?in|access|activat\w*|welcome)\b/i],
  ['Reports', /\b(reports?|reporting|data|exports?|price ?book|shelf tags?|analytics)\b/i]
];

export function categorize(text) {
  for (const [name, re] of RULES) if (re.test(text || '')) return name;
  return 'Other';
}
